// Mapeo FHIR del odontograma (033) — reemplaza a `extraerOdontologia`
// (formulario/extractors.ts, Patrón A, queda como legado sin uso): como 033
// pasa a Patrón B no hay `QuestionnaireResponse` de por medio, así que el
// propio componente arma estos recursos directo con `medplum.createResource`.
// Reusa el mismo sistema de código de pieza (`SISTEMA_PIEZA_DENTAL`) que ya
// usaba el extractor viejo — no se inventa uno nuevo. Funciones puras,
// testeadas sin Medplum real.
import type { Condition, Encounter, Observation, ObservationComponent, Reference } from '@medplum/fhirtypes';
import { CATALOGO_HALLAZGOS_ODONTOGRAMA, SUPERFICIES_DENTALES, type CodigoHallazgoOdontograma, type DatosOdontogramaPdf, type SuperficieDental } from '@logiacare/hce-pdf';
import type { EntradaOdontogramaUi, EstadoOdontograma } from './tipos';

export const SISTEMA_PIEZA_DENTAL = 'http://logiacare.ec/fhir/pieza-dental-fdi';
export const SISTEMA_HALLAZGO_ODONTOGRAMA = 'http://logiacare.ec/fhir/odontograma-hallazgo';
export const SISTEMA_CAMPO_ODONTOGRAMA = 'http://logiacare.ec/fhir/odontograma-campo';
export const SISTEMA_SUPERFICIE_DENTAL = 'http://logiacare.ec/fhir/superficie-dental';

const NOMBRE_SUPERFICIE: Record<SuperficieDental, string> = { V: 'Vestibular', L: 'Lingual/palatina', M: 'Mesial', D: 'Distal', O: 'Oclusal/incisal' };

/** Superficies con dato en una entrada, en el orden fijo V-L-M-D-O. */
function superficiesConDato(dato: EntradaOdontogramaUi): [SuperficieDental, 'caries' | 'obturado'][] {
  return SUPERFICIES_DENTALES.flatMap((s) => {
    const estado = dato.superficies?.[s];
    return estado ? [[s, estado] as [SuperficieDental, 'caries' | 'obturado']] : [];
  });
}

/**
 * Hallazgos "consumados" (tratamiento ya hecho o pieza ya perdida) — no se
 * listan como `Condition` activa del paciente, a diferencia de un hallazgo
 * pendiente (caries, "…indicada", etc.). Mismo criterio que el extractor
 * viejo excluía "Sano" del problem list.
 */
const HALLAZGOS_SIN_CONDITION_ACTIVA = new Set<CodigoHallazgoOdontograma>([
  'sano',
  'sellante-realizado',
  'obturado',
  'ausente',
  'perdida-otra-causa',
  'perdida-caries',
  'protesis-total-realizada',
  'protesis-fija-realizada',
  'protesis-removible-realizada',
  'corona-realizada',
  'endodoncia-realizada',
]);

/** Piezas con algún dato capturado (hallazgo≠sano, recesión o movilidad) — base común para el `Observation`, las `Condition` y el PDF. */
export function entradasConDato(estado: EstadoOdontograma): [string, EntradaOdontogramaUi][] {
  return Object.entries(estado).filter(([, e]) => (e.hallazgo && e.hallazgo !== 'sano') || e.recesion || e.movilidad || superficiesConDato(e).length > 0);
}

function componentesDePieza(pieza: string, dato: EntradaOdontogramaUi): ObservationComponent[] {
  const codingPieza = { system: SISTEMA_PIEZA_DENTAL, code: pieza, display: `Pieza ${pieza}` };
  const componentes: ObservationComponent[] = [];
  if (dato.hallazgo) {
    componentes.push({
      code: { coding: [codingPieza, { system: SISTEMA_CAMPO_ODONTOGRAMA, code: 'hallazgo' }] },
      valueCodeableConcept: { coding: [{ system: SISTEMA_HALLAZGO_ODONTOGRAMA, code: dato.hallazgo, display: CATALOGO_HALLAZGOS_ODONTOGRAMA[dato.hallazgo].etiqueta }] },
    });
  }
  for (const [superficie, estadoSup] of superficiesConDato(dato)) {
    componentes.push({
      code: { coding: [codingPieza, { system: SISTEMA_SUPERFICIE_DENTAL, code: superficie, display: NOMBRE_SUPERFICIE[superficie] }] },
      valueCodeableConcept: { coding: [{ system: SISTEMA_HALLAZGO_ODONTOGRAMA, code: estadoSup, display: CATALOGO_HALLAZGOS_ODONTOGRAMA[estadoSup].etiqueta }] },
    });
  }
  if (dato.recesion) {
    componentes.push({ code: { coding: [codingPieza, { system: SISTEMA_CAMPO_ODONTOGRAMA, code: 'recesion' }] }, valueInteger: dato.recesion });
  }
  if (dato.movilidad) {
    componentes.push({ code: { coding: [codingPieza, { system: SISTEMA_CAMPO_ODONTOGRAMA, code: 'movilidad' }] }, valueInteger: dato.movilidad });
  }
  return componentes;
}

/** Arma el `Observation` único del odontograma (un `component` por (pieza, campo) con dato). `undefined` si no hay nada capturado — no crear un recurso vacío. */
export function construirObservationOdontograma(estado: EstadoOdontograma, subject: Observation['subject'], encounter: Reference<Encounter> | undefined): Observation | undefined {
  const entradas = entradasConDato(estado);
  if (entradas.length === 0) return undefined;
  return {
    resourceType: 'Observation',
    status: 'final',
    code: { text: 'Odontograma' },
    subject,
    encounter,
    effectiveDateTime: new Date().toISOString(),
    component: entradas.flatMap(([pieza, dato]) => componentesDePieza(pieza, dato)),
  };
}

export interface DatoCondicionOdontograma {
  readonly pieza: string;
  readonly hallazgo: CodigoHallazgoOdontograma;
  /** Superficies afectadas (solo caries por superficie); vacío = pieza completa. */
  readonly superficies: readonly SuperficieDental[];
}

/** Piezas cuyo hallazgo amerita una `Condition` visible en el problem list del paciente (excluye "sano" y los estados ya consumados). */
export function condicionesActivas(estado: EstadoOdontograma): DatoCondicionOdontograma[] {
  const resultado: DatoCondicionOdontograma[] = [];
  for (const [pieza, dato] of entradasConDato(estado)) {
    const cariadas = superficiesConDato(dato).filter(([, e]) => e === 'caries').map(([s]) => s);
    if (cariadas.length > 0) resultado.push({ pieza, hallazgo: 'caries', superficies: cariadas });
    // Si la caries ya salió por superficie, el 'caries' de pieza completa no se duplica.
    if (dato.hallazgo && !HALLAZGOS_SIN_CONDITION_ACTIVA.has(dato.hallazgo) && !(dato.hallazgo === 'caries' && cariadas.length > 0)) {
      resultado.push({ pieza, hallazgo: dato.hallazgo, superficies: [] });
    }
  }
  return resultado;
}

/** `bodySite` de la `Condition` de una pieza: la pieza y, si aplica, cada superficie afectada. */
export function bodySiteCondicion(pieza: string, superficies: readonly SuperficieDental[]): NonNullable<Condition['bodySite']> {
  return [
    { coding: [{ system: SISTEMA_PIEZA_DENTAL, code: pieza, display: `Pieza ${pieza}` }] },
    ...superficies.map((s) => ({ coding: [{ system: SISTEMA_SUPERFICIE_DENTAL, code: s, display: NOMBRE_SUPERFICIE[s] }] })),
  ];
}

/** Wire format disperso para `EspecificacionPdf` (modo 'msp', campo `diagramaOdontograma`) — ver `odontograma.ts` en `@logiacare/hce-pdf`. */
export function datosOdontogramaPdf(estado: EstadoOdontograma): DatosOdontogramaPdf {
  return entradasConDato(estado).map(([pieza, dato]) => {
    const superficies = Object.fromEntries(superficiesConDato(dato));
    return { pieza, hallazgo: dato.hallazgo, recesion: dato.recesion, movilidad: dato.movilidad, ...(Object.keys(superficies).length > 0 ? { superficies } : {}) };
  });
}
